import React, { useEffect, useState } from 'react';
import { Button } from '@redpanda/ui';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Textarea, CustomSelect, LoadingButton } from '@redpanda/ui';
import FormFieldWithError from './forms/FormFieldWithError';
import { startPeriodClosing } from '../services/api';
import { toast } from 'react-hot-toast';

// Note: Validation messages are defined inline and translated via t() in the component
const periodClosingSchema = z.object({
  periodId: z.string().min(1),
  type: z.enum(['MONTHLY', 'QUARTERLY', 'YEARLY']),
  notes: z.string().optional(),
});

type PeriodClosingFormData = z.infer<typeof periodClosingSchema>;

interface PeriodClosingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  availablePeriods: Array<{
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    status: string;
    fiscalYear: {
      name: string;
    };
  }>;
  onClosingStarted: (closing?: any) => void;
}

const PeriodClosingDialog: React.FC<PeriodClosingDialogProps> = ({
  isOpen,
  onClose,
  availablePeriods,
  onClosingStarted
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<PeriodClosingFormData>({
    resolver: zodResolver(periodClosingSchema),
    defaultValues: {
      periodId: '',
      type: 'MONTHLY',
      notes: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        periodId: '',
        type: 'MONTHLY',
        notes: '',
      });
      setSubmitError('');
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: PeriodClosingFormData) => {
    setLoading(true);
    setSubmitError('');

    try {
      const response = await startPeriodClosing(data);
      const closing = response.data;

      if (closing.status === 'COMPLETED') {
        toast.success(t('periods.toast.closingCompleted'));
      } else if (closing.status === 'FAILED') {
        toast.error(t('periods.toast.closingFailed'));
      } else {
        toast.success(t('periods.toast.closingStarted'));
      }

      onClosingStarted(closing);
    } catch (error: any) {
      console.error('Error starting period closing:', error);
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          t('periods.toast.closingError');

      // 只顯示 Toast，不在對話框內顯示錯誤
      toast.error(errorMessage);
      // 錯誤時不要執行 onClosingStarted 或關閉對話框
      return;
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  // 轉換期間選項格式
  const openPeriods = availablePeriods.filter(period => period.status === 'OPEN');
  const periodOptions = openPeriods.map(period => ({
    value: period.id,
    label: `${period.fiscalYear.name} - ${period.name} (${period.startDate.split('T')[0]} ~ ${period.endDate.split('T')[0]})`
  }));

  const typeOptions = [
    { value: 'MONTHLY', label: t('periods.closingTypes.MONTHLY') },
    { value: 'QUARTERLY', label: t('periods.closingTypes.QUARTERLY') },
    { value: 'YEARLY', label: t('periods.closingTypes.YEARLY') }
  ];

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('periods.closing.dialog.title')}
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

            <FormFieldWithError
              label={t('periods.closing.dialog.labels.period')}
              required={true}
              error={errors.periodId?.message}
              htmlFor="periodId"
            >
              <CustomSelect
                id="periodId"
                options={periodOptions}
                placeholder={periodOptions.length === 0 ? t('periods.closing.dialog.placeholders.noPeriods') : t('periods.closing.dialog.placeholders.selectPeriod')}
                value={watch('periodId')}
                onChange={(value) => setValue('periodId', value)}
                variant={errors.periodId ? "error" : "default"}
                disabled={loading || periodOptions.length === 0}
              />
              {periodOptions.length === 0 && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-700">
                    {t('periods.closing.dialog.warnings.noPeriodsTitle')}
                    <br />• {t('periods.closing.dialog.warnings.hasYears')}
                    <br />• {t('periods.closing.dialog.warnings.periodOpen')}
                  </p>
                </div>
              )}
            </FormFieldWithError>

            <FormFieldWithError
              label={t('periods.closing.dialog.labels.type')}
              required={true}
              error={errors.type?.message}
              htmlFor="type"
            >
              <CustomSelect
                id="type"
                options={typeOptions}
                placeholder={t('common.placeholders.selectClosingType')}
                value={watch('type')}
                onChange={(value) => setValue('type', value as 'MONTHLY' | 'QUARTERLY' | 'YEARLY')}
                variant={errors.type ? "error" : "default"}
                disabled={loading}
              />
            </FormFieldWithError>

            <FormFieldWithError
              label={t('periods.closing.dialog.labels.notes')}
              required={false}
              error={errors.notes?.message}
              htmlFor="notes"
            >
              <Textarea
                id="notes"
                rows={3}
                {...register('notes')}
                variant={errors.notes ? "error" : "default"}
                placeholder={t('common.placeholders.closingNotes')}
                disabled={loading}
              />
            </FormFieldWithError>

            {submitError && (
              <div className="text-red-600 dark:text-red-400 text-sm mb-4">
                {submitError}
              </div>
            )}

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
              <div className="text-yellow-800 dark:text-yellow-200 text-sm">
                <p className="font-medium">{t('periods.closing.dialog.warnings.importantTitle')}</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>{t('periods.closing.dialog.warnings.monthly')}</li>
                  <li><strong>{t('periods.closing.dialog.warnings.yearly')}</strong></li>
                  <li>{t('periods.closing.dialog.warnings.autoTrialBalance')}</li>
                  <li>{t('periods.closing.dialog.warnings.balanceRequired')}</li>
                  <li>{t('periods.closing.dialog.warnings.reopenCondition')}</li>
                </ul>
              </div>
            </div>

            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse pt-6 border-t border-gray-200 dark:border-slate-700">
              <LoadingButton
                type="submit"
                loading={loading}
                className="w-full sm:w-auto sm:ml-3 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
                disabled={periodOptions.length === 0}
              >
                {t('periods.closing.dialog.actions.startClosing')}
              </LoadingButton>
              <Button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="mt-3 w-full sm:mt-0 sm:w-auto bg-white hover:bg-slate-50 text-slate-900 border border-slate-300 font-bold py-2 px-4 rounded transition-all duration-200"
              >
                {t('periods.closing.dialog.actions.cancel')}
              </Button>
            </div>

            {periodOptions.length === 0 && (
              <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
                {t('periods.closing.dialog.messages.noOpenPeriods')}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default PeriodClosingDialog;